import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  useErrorDialog,
  useToast,
  type ErrorDetail,
} from '@dayon/design-system';
import { AUDIT_REASON_MIN_LENGTH, type ReleaseOutcome } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import {
  ACTION_WORDS,
  reasonProblem,
  reasonReady,
  tellOutcome,
  type UnparkAction,
} from './payout-state.ts';

/**
 * THE OPERATOR'S ACTION ON A PARKED PAYOUT — the confirmation, the reason, and the press.
 *
 * THE WRITE:
 *   POST /api/withdrawals/:id/payout/unpark   QUERY · CONFIRM_PAID · REFUND · RESEND_SAME_ID ·
 *                                             DISMISS_HOLD, behind funds.approve; all but the
 *                                             query carry a reason
 *
 * ONE DIALOG FOR THE FIVE ACTIONS, its words from ACTION_WORDS (payout-state.ts), which
 * `destructive-confirmations.test.ts` reads under every title it can carry. Not
 * dismissible: a stray Escape would drop a decision and a typed reason without a word,
 * and three of these move money or settle the row on the operator's say-so.
 *
 * WHAT HAPPENED IS TOLD AS WHAT IT WAS (`tellOutcome`). A query or a re-send that left the
 * payout refused and refunded, parked or unanswered opens the error dialog with the API's
 * sentence; a success is a toast; a refusal is the error dialog, titled with what did not
 * happen. A reason the route refuses goes back beside the field instead.
 *
 * The panel that opens this re-reads its own record, and the drawer re-reads its row,
 * whatever the answer was (`onSettled`): an action that was refused may have been refused
 * because the row moved under it, and the drawer must show where it stands now.
 */

/** What the error dialog is titled when an action does not go through; the message is the API's. */
const NOT_DONE: Record<UnparkAction, (ref: string) => string> = {
  QUERY: (ref) => `Asking StarPay about ${ref} did not finish`,
  CONFIRM_PAID: (ref) => `Could not confirm ${ref} as paid`,
  REFUND: (ref) => `Could not refund ${ref}`,
  RESEND_SAME_ID: (ref) => `Could not re-send ${ref}`,
  DISMISS_HOLD: (ref) => `Could not close the hold on ${ref}`,
};

type Tell = { toast: (message: string) => void; showError: (detail: ErrorDetail) => void };

/**
 * Send one action, and tell what came of it. `done` is whether the route took it; a reason
 * the route refused comes back as `reasonError`, for the field, and is told nowhere else.
 */
export async function runPayoutAction(args: {
  withdrawalId: string;
  ref: string;
  action: UnparkAction;
  reason: string;
  tell: Tell;
}): Promise<{ done: boolean; reasonError?: string }> {
  const { withdrawalId, ref, action, tell } = args;
  try {
    const told = await api.post<ReleaseOutcome>(
      `/api/withdrawals/${encodeURIComponent(withdrawalId)}/payout/unpark`,
      action === 'QUERY' ? { action } : { action, reason: args.reason.trim() },
    );
    tellOutcome(action, ref, told, tell);
    return { done: true };
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && error.fields?.['reason']) {
      return { done: false, reasonError: error.fields['reason'] };
    }
    tell.showError({
      title: NOT_DONE[action](ref),
      message:
        error instanceof ApiRequestError
          ? error.message
          : 'The action did not complete. Try again shortly.',
      ...(error instanceof ApiRequestError ? { code: error.code } : {}),
    });
    return { done: false };
  }
}

export function PayoutActionDialog({
  acting,
  withdrawalId,
  withdrawalRef,
  onClose,
  onSettled,
}: {
  /** The action being confirmed, or null when the dialog is closed. */
  acting: UnparkAction | null;
  withdrawalId: string;
  withdrawalRef: string;
  onClose: () => void;
  /** After the route answered, whatever it answered: `done` is whether the action went through. */
  onSettled: (done: boolean) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  /* A reason typed for one action is never waiting in the box for the next: cleared whenever the
     dialog opens on an action, adjusted during render rather than in an effect. */
  const [openedOn, setOpenedOn] = useState<UnparkAction | null>(acting);
  if (openedOn !== acting) {
    setOpenedOn(acting);
    setReason('');
    setReasonError(undefined);
  }

  const takesReason = acting !== null && acting !== 'QUERY';
  const ready = !takesReason || reasonReady(reason);

  const act = async () => {
    if (acting === null || !ready || busy) return;
    setBusy(true);
    const result = await runPayoutAction({
      withdrawalId,
      ref: withdrawalRef,
      action: acting,
      reason,
      tell: { toast, showError },
    });
    setBusy(false);
    if (result.reasonError !== undefined) {
      setReasonError(result.reasonError);
      return;
    }
    onSettled(result.done);
  };

  return (
    <Dialog
      open={acting !== null}
      onClose={onClose}
      title={acting === null ? '' : ACTION_WORDS[acting].title}
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!ready || busy} onClick={() => void act()}>
            {acting === null ? '' : ACTION_WORDS[acting].button}
          </Button>
        </>
      }
    >
      {acting !== null ? (
        <div className="stack">
          <div
            className={
              ACTION_WORDS[acting].tone === 'danger' ? 'notice notice-danger' : 'notice notice-warn'
            }
          >
            <Icon name="warning" />
            <div>{ACTION_WORDS[acting].consequence}</div>
          </div>
          {takesReason ? (
            <Field
              label="Reason"
              hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters — what you checked, and with whom. It goes on the audit trail with your name.`}
              error={reasonError ?? reasonProblem(reason)}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  minLength={AUDIT_REASON_MIN_LENGTH}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError(undefined);
                  }}
                />
              )}
            </Field>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
